import type {
  GSplatStreamingConfiguration,
  GSplatSystemRuntimeControl,
} from '@/plugins/gsplat/GSplatSystem'
import type { GSplatDebugMode, GSplatParams } from '@/plugins/gsplat/unified/GSplatParams'

/** 唯一 Unified GSplatParams/Director 与 GSplatSystem 高层编排器之间的窄适配。 */
export class GSplatParamsRuntimeControl implements GSplatSystemRuntimeControl {
  constructor(
    private readonly params: GSplatParams,
    private readonly readRenderedSplatCount: () => number = () => 0
  ) {}

  prewarmClip(maxVertices: number, maxVolumes = 1): boolean {
    return this.params.prewarmClip(maxVertices, maxVolumes)
  }

  releaseClipPrewarm(): boolean {
    return this.params.releaseClipPrewarm()
  }

  getStreamingConfiguration(): GSplatStreamingConfiguration {
    return Object.freeze({
      radialSorting: this.params.radialSorting,
      lodUpdateAngle: this.params.lodUpdateAngle,
      lodBehindPenalty: this.params.lodBehindPenalty,
      lodUpdateDistance: this.params.lodUpdateDistance,
      lodUnderfillLimit: this.params.lodUnderfillLimit,
      lodRefinementMode: this.params.lodRefinementMode ?? 'step',
    })
  }

  setStreamingConfiguration(config: Readonly<GSplatStreamingConfiguration>): void {
    this.validate(config)
    this.params.radialSorting = config.radialSorting
    this.params.lodUpdateAngle = config.lodUpdateAngle
    this.params.lodBehindPenalty = config.lodBehindPenalty
    this.params.lodUpdateDistance = config.lodUpdateDistance
    this.params.lodUnderfillLimit = config.lodUnderfillLimit
    this.params.lodRefinementMode = config.lodRefinementMode ?? 'step'
  }

  getDebugMode(): GSplatDebugMode {
    return this.params.debug
  }

  setDebugMode(mode: GSplatDebugMode): void {
    this.params.debug = mode
  }

  getRenderedSplatCount(): number {
    return this.readRenderedSplatCount()
  }

  private validate(config: Readonly<GSplatStreamingConfiguration>): void {
    if (!Number.isFinite(config.lodUpdateAngle) || config.lodUpdateAngle < 0) {
      throw new Error(`非法 GSplat lodUpdateAngle: ${config.lodUpdateAngle}`)
    }
    if (!Number.isFinite(config.lodBehindPenalty) || config.lodBehindPenalty < 1) {
      throw new Error(`非法 GSplat lodBehindPenalty: ${config.lodBehindPenalty}`)
    }
    if (!Number.isFinite(config.lodUpdateDistance) || config.lodUpdateDistance < 0) {
      throw new Error(`非法 GSplat lodUpdateDistance: ${config.lodUpdateDistance}`)
    }
    if (!Number.isSafeInteger(config.lodUnderfillLimit) || config.lodUnderfillLimit < 0) {
      throw new Error(`非法 GSplat lodUnderfillLimit: ${config.lodUnderfillLimit}`)
    }
    if (
      config.lodRefinementMode !== undefined &&
      config.lodRefinementMode !== 'step' &&
      config.lodRefinementMode !== 'direct'
    ) {
      throw new Error(`非法 GSplat lodRefinementMode: ${config.lodRefinementMode}`)
    }
  }
}
